import { useState, useEffect, useRef, useCallback } from 'react';
import { PasswordInput } from '../components/PasswordInput';

const SERVER_URL = window.location.origin;

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch (e) {}
}

function showBrowserNotification(title, body) {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    try {
      const n = new Notification(title, { body, icon: '/favicon.ico' });
      n.onclick = () => { window.focus(); n.close(); };
    } catch (e) {}
  }
}

export function AgentHomePage({ socket, token, agent, onLogout, onAcceptSession }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileFullName, setProfileFullName] = useState(agent?.fullName || '');
  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  const [avatarError, setAvatarError] = useState(false);
  const [requests, setRequests] = useState([]);
  const [rejectingCode, setRejectingCode] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [incomingTransfer, setIncomingTransfer] = useState(null);
  const [decliningTransfer, setDecliningTransfer] = useState(false);
  const knownCodesRef = useRef(new Set());

  const [overviewSessions, setOverviewSessions] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewSearch, setOverviewSearch] = useState('');

  const [viewingScreenshot, setViewingScreenshot] = useState(null);
  const [screenshotComment, setScreenshotComment] = useState('');
  const [openSessionShots, setOpenSessionShots] = useState(null);
  const [sessionShotsLoading, setSessionShotsLoading] = useState(false);

  const [openChatSession, setOpenChatSession] = useState(null);
  const [chatMessagesLoading, setChatMessagesLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!socket || !token) return;
    socket.emit('agent:hello', { token });

    socket.on('requests:updated', (list) => {
      const waitingCodes = list.filter(r => r.status === 'waiting').map(r => r.code);
      const newCodes = waitingCodes.filter(c => !knownCodesRef.current.has(c));
      if (newCodes.length > 0 && knownCodesRef.current.size > 0) {
        playNotificationSound();
        showBrowserNotification('Novi zahtev za podršku', `Stigao je novi zahtev (${newCodes.length})`);
      }
      knownCodesRef.current = new Set(list.map(r => r.code));
      setRequests(list);
    });

    socket.on('agent:accepted', ({ code }) => {
      onAcceptSession(code);
    });

    socket.on('transfer:incoming', (data) => {
      setIncomingTransfer(data);
      playNotificationSound();
      showBrowserNotification('Transfer sesije', `Agent ${data.fromFullName} vam prebacuje sesiju`);
    });

    return () => {
      socket.off('requests:updated');
      socket.off('agent:accepted');
      socket.off('transfer:incoming');
    };
  }, [socket, token, onAcceptSession]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadOverviewSessions = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/admin/sessions-overview?limit=300`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setOverviewSessions(await res.json());
      } else {
        setOverviewSessions([]);
      }
    } catch (e) {
      console.warn('[Overview] Greška pri učitavanju:', e);
    } finally {
      setOverviewLoading(false);
    }
  }, [token]);

  useEffect(() => { loadOverviewSessions(); }, [loadOverviewSessions]);

  const openSessionScreenshots = async (sessionCode) => {
    setSessionShotsLoading(true);
    setOpenSessionShots({ code: sessionCode, shots: [] });
    try {
      const res = await fetch(`${SERVER_URL}/api/screenshots?session=${encodeURIComponent(sessionCode)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const shots = await res.json();
        setOpenSessionShots({ code: sessionCode, shots });
      }
    } catch (e) {
      console.warn('[Screenshots] Greška pri učitavanju snimaka sesije:', e);
    } finally {
      setSessionShotsLoading(false);
    }
  };

  const openSessionChat = async (sessionCode) => {
    setChatMessagesLoading(true);
    setOpenChatSession({ code: sessionCode, messages: [] });
    try {
      const res = await fetch(`${SERVER_URL}/api/chat-messages?session=${encodeURIComponent(sessionCode)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const messages = await res.json();
        setOpenChatSession({ code: sessionCode, messages });
      }
    } catch (e) {
      console.warn('[Chat] Greška pri učitavanju poruka:', e);
    } finally {
      setChatMessagesLoading(false);
    }
  };

  const deleteScreenshot = async (id) => {
    if (!window.confirm('Obrisati ovaj snimak?')) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/screenshots/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setOpenSessionShots(prev => prev ? { ...prev, shots: prev.shots.filter(s => s.id !== id) } : prev);
        setViewingScreenshot(null);
        loadOverviewSessions();
      } else {
        const body = await res.json();
        alert(body.error || 'Greška pri brisanju');
      }
    } catch (e) {
      console.error('[Screenshots] Greška pri brisanju:', e);
    }
  };

  const saveScreenshotComment = async (id) => {
    try {
      await fetch(`${SERVER_URL}/api/screenshots/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ comment: screenshotComment })
      });
      setOpenSessionShots(prev => prev ? {
        ...prev,
        shots: prev.shots.map(s => s.id === id ? { ...s, comment: screenshotComment } : s)
      } : prev);
    } catch (e) {
      console.error('[Screenshots] Greška pri čuvanju komentara:', e);
    }
  };

  const handleAccept = (code) => {
    socket.emit('agent:accept-request', { code, token });
  };

  const handleReject = (code, reason) => {
    socket.emit('agent:reject-request', { code, reason, token });
    setRejectingCode(null);
  };

  const handleTransferAccept = () => {
    if (!incomingTransfer) return;
    socket.emit('agent:transfer-response', { code: incomingTransfer.code, accepted: true, token });
    onAcceptSession(incomingTransfer.code);
    setIncomingTransfer(null);
  };

  const handleTransferDeclineSubmit = (reason) => {
    if (!incomingTransfer) return;
    socket.emit('agent:transfer-response', { code: incomingTransfer.code, accepted: false, reason, token });
    setIncomingTransfer(null);
    setDecliningTransfer(false);
  };

  const formatWait = (isoTime) => {
    if (!isoTime) return '—';
    const seconds = Math.floor((now - new Date(isoTime).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const waitColor = (isoTime) => {
    const seconds = Math.floor((now - new Date(isoTime).getTime()) / 1000);
    if (seconds < 120) return '#1D9E75';
    if (seconds < 300) return '#EF9F27';
    return '#E24B4A';
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'waiting': return { text: 'Čeka', color: '#EF9F27', bg: '#FAEEDA' };
      case 'accepted': return { text: 'Prihvaćen', color: '#0C447C', bg: '#E6F1FB' };
      case 'sharing': return { text: 'Deli ekran', color: '#085041', bg: '#E1F5EE' };
      case 'client-offline': return { text: 'Klijent offline', color: '#666', bg: '#f0f0f0' };
      default: return { text: status, color: '#666', bg: '#f0f0f0' };
    }
  };

  const s = {
    wrap: { minHeight: '100vh', background: '#f5f5f5', fontFamily: 'sans-serif' },
    topbar: {
      background: '#fff', borderBottom: '1px solid #e5e5e5', padding: '14px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    },
    title: { fontSize: 18, fontWeight: 700, color: '#111' },
    topRight: { display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: '#666' },
    btn: (v) => ({
      padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
      background: v === 'primary' ? '#185FA5' : v === 'danger' ? '#E24B4A' : v === 'success' ? '#1D9E75' : '#f0f0f0',
      color: (v && v !== 'default') ? '#fff' : '#333'
    }),
    container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    sectionTitle: { fontSize: 16, fontWeight: 600, color: '#333' },
    empty: {
      background: '#fff', borderRadius: 10, padding: 60, textAlign: 'center',
      color: '#999', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 },
    card: {
      background: '#fff', borderRadius: 10, padding: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 10
    },
    cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 },
    code: { fontSize: 22, fontWeight: 700, fontFamily: 'monospace', letterSpacing: 2, color: '#111' },
    statusBadge: (color, bg) => ({
      display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11,
      fontWeight: 600, background: bg, color
    }),
    description: {
      background: '#fafafa', borderRadius: 6, padding: '8px 10px', fontSize: 13, color: '#333',
      minHeight: 44, lineHeight: 1.4, fontStyle: 'normal'
    },
    descriptionEmpty: {
      background: '#fafafa', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#999',
      minHeight: 44, fontStyle: 'italic', display: 'flex', alignItems: 'center'
    },
    meta: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666' },
    waitBadge: (color) => ({ fontWeight: 600, color }),
    actions: { display: 'flex', gap: 6, marginTop: 4 },
    modalOverlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100
    },
    modal: {
      background: '#fff', borderRadius: 12, padding: 24, width: '100%',
      maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.16)'
    },
    modalTitle: { fontSize: 17, fontWeight: 700, marginBottom: 16, color: '#111', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    reasonBtn: (selected) => ({
      display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
      background: selected ? '#E6F1FB' : '#f5f5f5', border: selected ? '1px solid #185FA5' : '1px solid #e5e5e5',
      borderRadius: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13
    }),
    modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
    transferInfo: { background: '#FAF3E8', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#8A5A1F', marginBottom: 12 },
    transferMsg: { background: '#fafafa', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#333', marginBottom: 12, fontStyle: 'italic' },

    filterRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
    filterInput: { padding: '7px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', minWidth: 160 },
    shotsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
    shotCard: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: 'pointer' },
    shotImg: { width: '100%', height: 160, objectFit: 'cover', background: '#000', display: 'block' },
    shotInfo: { padding: '8px 10px' },
    shotCode: { fontSize: 12, fontWeight: 700, fontFamily: 'monospace' },
    shotMeta: { fontSize: 11, color: '#888', marginTop: 2 },
    tableWrap: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #eee', background: '#fafafa', fontWeight: 600, color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 },
    td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', color: '#333', verticalAlign: 'middle' },
    countBadge: { display: 'inline-block', padding: '2px 9px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: '#E6F1FB', color: '#0C447C' },
    sessionShotsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginTop: 4 },
    screenshotModal: { background: '#fff', borderRadius: 12, padding: '20px 20px 28px', width: '90vw', maxWidth: 1100, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflowY: 'auto' },
    screenshotFullImg: { maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 8, background: '#000' },
    commentInput: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginTop: 10, boxSizing: 'border-box' },
    closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999' },

    chatModal: { background: '#fff', borderRadius: 12, padding: 20, width: '90vw', maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    chatMessagesWrap: { flex: 1, overflowY: 'auto', padding: '10px 4px', display: 'flex', flexDirection: 'column', gap: 8, background: '#fafafa', borderRadius: 8 },
    chatBubble: (fromAgent) => ({
      maxWidth: '75%', padding: '8px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.4,
      alignSelf: fromAgent ? 'flex-start' : 'flex-end',
      background: fromAgent ? '#E6F1FB' : '#DCF8C6', color: '#222'
    }),
    chatMeta: { fontSize: 10, color: '#999', marginTop: 3 }
  };

  const waitingRequests = requests.filter(r => r.status === 'waiting' || r.status === 'client-offline');
  const activeRequests = requests.filter(r => r.status === 'accepted' || r.status === 'sharing');

  const sessionsWithContent = overviewSessions.filter(sess => {
    const hasContent = sess.screenshot_count > 0 || sess.message_count > 0;
    if (!hasContent) return false;
    if (!overviewSearch.trim()) return true;
    const q = overviewSearch.trim().toLowerCase();
    return sess.code.toLowerCase().includes(q) ||
      (sess.agent_username || '').toLowerCase().includes(q);
  });

  const canDeleteScreenshot = (shot) =>
    agent?.role === 'admin' || shot.agent_username === agent?.username;

  const formatShotDate = (iso) => new Date(iso).toLocaleString('sr-RS', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <div style={s.title}>Screen Support</div>
        <div style={s.topRight}>
          {!avatarError && agent?.username && (
            <img
              src={`${SERVER_URL}/api/avatar/${agent.username}?v=${avatarVersion}`}
              onError={() => setAvatarError(true)}
              alt=""
              style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ddd' }}
            />
          )}
          <span>{profileFullName || agent?.fullName || agent?.username}</span>
          {agent?.role === 'admin' && (
            <a href="/admin" style={{ color: '#185FA5', textDecoration: 'none' }}>Admin panel</a>
          )}
          <button style={s.btn()} onClick={() => setProfileOpen(true)}>Moj profil</button>
          <button style={s.btn()} onClick={onLogout}>Odjavi se</button>
        </div>
      </div>

      <div style={s.container}>
        <div style={s.headerRow}>
          <div style={s.sectionTitle}>Zahtevi na čekanju ({waitingRequests.length})</div>
        </div>

        {waitingRequests.length === 0 ? (
          <div style={s.empty}>Trenutno nema zahteva. Kad klijent podnese zahtev, ovde ce se pojaviti.</div>
        ) : (
          <div style={s.grid}>
            {waitingRequests.map(r => {
              const status = statusLabel(r.status);
              return (
                <div key={r.code} style={s.card}>
                  <div style={s.cardTop}>
                    <div>
                      <div style={s.code}>{r.code}</div>
                      {r.deviceModel && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{r.deviceModel}</div>}
                    </div>
                    <span style={s.statusBadge(status.color, status.bg)}>{status.text}</span>
                  </div>

                  {r.description ? (
                    <div style={s.description}>{r.description}</div>
                  ) : (
                    <div style={s.descriptionEmpty}>Klijent nije naveo opis problema.</div>
                  )}

                  <div style={s.meta}>
                    <span>Ceka: <span style={s.waitBadge(waitColor(r.requestedAt))}>{formatWait(r.requestedAt)}</span></span>
                  </div>

                  <div style={s.actions}>
                    <button
                      style={{ ...s.btn('success'), flex: 1 }}
                      disabled={r.status === 'client-offline'}
                      onClick={() => handleAccept(r.code)}
                    >
                      Preuzmi
                    </button>
                    <button style={s.btn('danger')} onClick={() => setRejectingCode(r.code)}>
                      Odbaci
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeRequests.length > 0 && (
          <>
            <div style={{ ...s.headerRow, marginTop: 32 }}>
              <div style={s.sectionTitle}>Aktivne sesije ({activeRequests.length})</div>
            </div>
            <div style={s.grid}>
              {activeRequests.map(r => {
                const status = statusLabel(r.status);
                const isMine = r.agentUsername === agent?.username;
                return (
                  <div key={r.code} style={{ ...s.card, opacity: isMine ? 1 : 0.7 }}>
                    <div style={s.cardTop}>
                      <div>
                        <div style={s.code}>{r.code}</div>
                        <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                          Agent: {r.agentUsername || '—'}
                        </div>
                      </div>
                      <span style={s.statusBadge(status.color, status.bg)}>{status.text}</span>
                    </div>
                    {r.description && <div style={s.description}>{r.description}</div>}
                    {isMine && (
                      <button style={{ ...s.btn('primary'), width: '100%', marginTop: 4 }} onClick={() => onAcceptSession(r.code)}>
                        Vrati se u sesiju
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={{ ...s.headerRow, marginTop: 32 }}>
          <div style={s.sectionTitle}>Sesije sa snimcima i chat-om ({sessionsWithContent.length})</div>
        </div>
        <div style={s.filterRow}>
          <input
            style={{ ...s.filterInput, minWidth: 260 }}
            placeholder="Pretraga po kodu sesije ili agentu..."
            value={overviewSearch}
            onChange={e => setOverviewSearch(e.target.value)}
          />
        </div>

        {overviewLoading && <div style={s.empty}>Učitavanje...</div>}
        {!overviewLoading && sessionsWithContent.length === 0 && (
          <div style={s.empty}>Nema sesija sa snimcima ili chat porukama.</div>
        )}
        {!overviewLoading && sessionsWithContent.length > 0 && (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Kod sesije</th>
                  <th style={s.th}>Agent</th>
                  <th style={s.th}>Opis problema</th>
                  <th style={s.th}>Uređaj</th>
                  <th style={s.th}>Zahtev</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Akcije</th>
                </tr>
              </thead>
              <tbody>
                {sessionsWithContent.map(sess => (
                  <tr key={sess.code}>
                    <td style={s.td}><strong style={{ fontFamily: 'monospace' }}>{sess.code}</strong></td>
                    <td style={s.td}>{sess.agent_username || '—'}</td>
                    <td style={{ ...s.td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {sess.description || <span style={{ color: '#999' }}>bez opisa</span>}
                    </td>
                    <td style={s.td}>{sess.device_model || '—'}</td>
                    <td style={s.td}>{formatShotDate(sess.requested_at)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>
                      {sess.screenshot_count > 0 && (
                        <button style={{ ...s.btn('primary'), marginLeft: 6 }} onClick={() => openSessionScreenshots(sess.code)}>
                          📷 Snimci ({sess.screenshot_count})
                        </button>
                      )}
                      {sess.message_count > 0 && (
                        <button style={{ ...s.btn('primary'), marginLeft: 6 }} onClick={() => openSessionChat(sess.code)}>
                          💬 Chat ({sess.message_count})
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rejectingCode && (
        <ReasonModal
          title={`Odbaci zahtev ${rejectingCode}`}
          submitLabel="Odbaci zahtev"
          onClose={() => setRejectingCode(null)}
          onSubmit={(reason) => handleReject(rejectingCode, reason)}
          styles={s}
        />
      )}

      {incomingTransfer && !decliningTransfer && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Transfer sesije</div>
            <div style={s.transferInfo}>
              Agent <strong>{incomingTransfer.fromFullName}</strong> vam prebacuje sesiju sa klijentom (kod {incomingTransfer.code}).
            </div>
            {incomingTransfer.deviceModel && (
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Uređaj: {incomingTransfer.deviceModel}</div>
            )}
            {incomingTransfer.description && (
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Opis problema: {incomingTransfer.description}</div>
            )}
            {incomingTransfer.message && (
              <div style={s.transferMsg}>"{incomingTransfer.message}"</div>
            )}
            <div style={s.modalActions}>
              <button style={s.btn('danger')} onClick={() => setDecliningTransfer(true)}>Odbij</button>
              <button style={s.btn('success')} onClick={handleTransferAccept}>Prihvati</button>
            </div>
          </div>
        </div>
      )}

      {incomingTransfer && decliningTransfer && (
        <ReasonModal
          title="Razlog odbijanja transfera"
          submitLabel="Odbij transfer"
          onClose={() => setDecliningTransfer(false)}
          onSubmit={handleTransferDeclineSubmit}
          styles={s}
        />
      )}

      {openSessionShots && !viewingScreenshot && (
        <div style={s.modalOverlay} onClick={() => setOpenSessionShots(null)}>
          <div style={s.screenshotModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>Snimci sesije {openSessionShots.code} ({openSessionShots.shots.length})</span>
              <button style={s.closeBtn} onClick={() => setOpenSessionShots(null)}>✕</button>
            </div>
            {sessionShotsLoading && <div style={s.empty}>Učitavanje...</div>}
            {!sessionShotsLoading && openSessionShots.shots.length === 0 && (
              <div style={s.empty}>Nema snimaka.</div>
            )}
            {!sessionShotsLoading && openSessionShots.shots.length > 0 && (
              <div style={s.sessionShotsGrid}>
                {openSessionShots.shots.map(shot => (
                  <div
                    key={shot.id}
                    style={s.shotCard}
                    onClick={() => { setViewingScreenshot(shot); setScreenshotComment(shot.comment || ''); }}
                  >
                    <img
                      src={`${SERVER_URL}/api/screenshots/${shot.id}/file?token=${token}`}
                      style={s.shotImg}
                      alt="screenshot"
                    />
                    <div style={s.shotInfo}>
                      <div style={s.shotMeta}>{formatShotDate(shot.created_at)}</div>
		      {shot.comment && (
			    <div style={{ fontSize: 12, color: '#555', marginTop: 4, wordBreak: 'break-word' }}>
		      		{shot.comment}
		    	    </div>
			  )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {viewingScreenshot && (
        <div style={s.modalOverlay} onClick={() => setViewingScreenshot(null)}>
          <div style={s.screenshotModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>{viewingScreenshot.session_code} — {new Date(viewingScreenshot.created_at).toLocaleString('sr-RS')}</span>
              <button style={s.closeBtn} onClick={() => setViewingScreenshot(null)}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Agent: {viewingScreenshot.agent_username || '—'}</div>
            <img
              src={`${SERVER_URL}/api/screenshots/${viewingScreenshot.id}/file?token=${token}`}
              style={s.screenshotFullImg}
              alt="screenshot"
            />
            <input
              style={s.commentInput}
              placeholder="Dodaj komentar..."
              value={screenshotComment}
              onChange={e => setScreenshotComment(e.target.value)}
              onBlur={() => saveScreenshotComment(viewingScreenshot.id)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              {canDeleteScreenshot(viewingScreenshot) ? (
                <button
                  style={{ ...s.btn('danger'), fontSize: 12 }}
                  onClick={() => deleteScreenshot(viewingScreenshot.id)}
                >
                  Obriši snimak
                </button>
              ) : <div />}
              
                <a href={`${SERVER_URL}/api/screenshots/${viewingScreenshot.id}/file?token=${token}`}
                download={`screenshot_${viewingScreenshot.id}.webp`}
                style={{ ...s.btn('primary'), textDecoration: 'none', display: 'inline-block' }}
              >
                Preuzmi
              </a>
            </div>
          </div>
        </div>
      )}

      {openChatSession && (
        <div style={s.modalOverlay} onClick={() => setOpenChatSession(null)}>
          <div style={s.chatModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>Chat - sesija {openChatSession.code} ({openChatSession.messages.length})</span>
              <button style={s.closeBtn} onClick={() => setOpenChatSession(null)}>✕</button>
            </div>
            <div style={s.chatMessagesWrap}>
              {chatMessagesLoading && <div style={s.empty}>Učitavanje...</div>}
              {!chatMessagesLoading && openChatSession.messages.length === 0 && (
                <div style={s.empty}>Nema poruka u ovoj sesiji.</div>
              )}
              {!chatMessagesLoading && openChatSession.messages.map(msg => (
                <div key={msg.id} style={s.chatBubble(msg.from_role === 'agent')}>
                  <div>{msg.text}</div>
                  <div style={s.chatMeta}>
                    {msg.from_role === 'agent' ? (msg.from_name || 'Agent') : 'Klijent'} · {new Date(msg.created_at).toLocaleString('sr-RS')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {profileOpen && (
        <ProfileModal
          agent={agent}
          token={token}
          serverUrl={SERVER_URL}
          onClose={() => setProfileOpen(false)}
          onNameSaved={(newName) => {
            setProfileFullName(newName);
            try {
              const stored = JSON.parse(localStorage.getItem('auth_agent') || '{}');
              stored.fullName = newName;
              localStorage.setItem('auth_agent', JSON.stringify(stored));
            } catch (e) {}
          }}
          onAvatarSaved={() => { setAvatarVersion(Date.now()); setAvatarError(false); }}
          styles={s}
        />
      )}
    </div>
  );
}

function ProfileModal({ agent, token, serverUrl, onClose, onNameSaved, onAvatarSaved, styles: s }) {
  const [fullName, setFullName] = useState(agent?.fullName || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState('');
  const [avatarLoadError, setAvatarLoadError] = useState(false);

  const saveProfile = async () => {
    if (!fullName.trim()) { setNameMsg('Ime ne može biti prazno'); return; }
    setNameSaving(true);
    setNameMsg('');
    try {
      const res = await fetch(`${serverUrl}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ fullName })
      });
      const body = await res.json();
      if (!res.ok) { setNameMsg(body.error || 'Greška'); return; }

      if (avatarPreview) {
        setAvatarSaving(true);
        try {
          const avatarRes = await fetch(`${serverUrl}/api/auth/avatar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ imageData: avatarPreview })
          });
          const avatarBody = await avatarRes.json();
          if (avatarRes.ok) {
            setAvatarFile(null);
            setAvatarLoadError(false);
            onAvatarSaved();
          }
        } finally {
          setAvatarSaving(false);
        }
      }

      setNameMsg('Sačuvano');
      onNameSaved(body.agent.fullName);
    } catch (e) {
      setNameMsg('Greška pri čuvanju');
    } finally {
      setNameSaving(false);
    }
  };

  const savePassword = async () => {
    setPasswordMsg('');
    if (!currentPassword || !newPassword) { setPasswordMsg('Popunite sva polja'); return; }
    if (newPassword.length < 6) { setPasswordMsg('Nova lozinka mora imati bar 6 znakova'); return; }
    if (newPassword !== confirmPassword) { setPasswordMsg('Nove lozinke se ne poklapaju'); return; }
    setPasswordSaving(true);
    try {
      const res = await fetch(`${serverUrl}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const body = await res.json();
      if (!res.ok) { setPasswordMsg(body.error || 'Greška'); return; }
      setPasswordMsg('Lozinka promenjena');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) {
      setPasswordMsg('Greška pri čuvanju');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveAvatar = async () => {
    if (!avatarPreview) return;
    setAvatarSaving(true);
    setAvatarMsg('');
    try {
      const res = await fetch(`${serverUrl}/api/auth/avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ imageData: avatarPreview })
      });
      const body = await res.json();
      if (!res.ok) { setAvatarMsg(body.error || 'Greška'); return; }
      setAvatarMsg('Slika sačuvana');
      setAvatarFile(null);
      setAvatarLoadError(false);
      onAvatarSaved();
    } catch (e) {
      setAvatarMsg('Greška pri čuvanju slike');
    } finally {
      setAvatarSaving(false);
    }
  };

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalTitle}>
          <span>Moj profil</span>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Korisničko ime</div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{agent?.username}</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd', flexShrink: 0 }}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : !avatarLoadError ? (
              <img
                src={`${serverUrl}/api/avatar/${agent?.username}?v=${Date.now()}`}
                onError={() => setAvatarLoadError(true)}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: 22, color: '#bbb' }}>—</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>Ime za prikaz</div>
            <input
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginBottom: 6, boxSizing: 'border-box' }}
              value={fullName}
              onChange={e => setFullName(e.target.value)}
            />
            <input type="file" accept="image/*" onChange={handleFileSelect} style={{ fontSize: 11 }} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: (nameMsg === 'Sačuvano') ? '#1D9E75' : '#E24B4A' }}>{nameMsg}</span>
          <button style={s.btn('primary')} onClick={saveProfile} disabled={nameSaving || avatarSaving}>
            {(nameSaving || avatarSaving) ? 'Čuvam...' : 'Snimi'}
          </button>
        </div>

        <div style={{ borderTop: '1px solid #eee', margin: '4px 0 16px' }} />

        <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>Promena lozinke</div>
        <PasswordInput
          placeholder="Trenutna lozinka"
          style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
        />
        <PasswordInput
          placeholder="Nova lozinka (min 6 znakova)"
          style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
        />
        <PasswordInput
          placeholder="Potvrdi novu lozinku"
          style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 7, outline: 'none', marginBottom: 6, boxSizing: 'border-box' }}
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: passwordMsg === 'Lozinka promenjena' ? '#1D9E75' : '#E24B4A' }}>{passwordMsg}</span>
          <button style={s.btn('primary')} onClick={savePassword} disabled={passwordSaving}>
            {passwordSaving ? 'Čuvam...' : 'Promeni lozinku'}
          </button>
        </div>

      </div>
    </div>
  );
}

function ReasonModal({ title, submitLabel, onClose, onSubmit, styles: s }) {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const reasons = [
    'Zauzet sam drugim poslom',
    'Nemam dovoljno konteksta',
    'Trenutno nisam za računarom',
    'Drugo'
  ];

  const handleSubmit = () => {
    const final = reason === 'Drugo' ? customReason.trim() : reason;
    if (!final) return;
    onSubmit(final);
  };

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalTitle}>{title}</div>
        {reasons.map(r => (
          <button key={r} style={s.reasonBtn(reason === r)} onClick={() => setReason(r)}>
            {r}
          </button>
        ))}
        {reason === 'Drugo' && (
          <input
            style={{
              width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid #ddd',
              borderRadius: 7, outline: 'none', marginTop: 4, boxSizing: 'border-box'
            }}
            placeholder="Opišite razlog"
            value={customReason}
            onChange={e => setCustomReason(e.target.value)}
            autoFocus
          />
        )}
        <div style={s.modalActions}>
          <button style={s.btn()} onClick={onClose}>Otkaži</button>
          <button
            style={s.btn('danger')}
            onClick={handleSubmit}
            disabled={!reason || (reason === 'Drugo' && !customReason.trim())}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
